import { ModalOpenButton } from "@msys/ui";
import { Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ItemType } from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { rootItemsAllowedConstraints } from "../../doc-items/constraints.js";
import {
  ItemDetailHintBar,
  useLocalStorageItemHintDismissal,
} from "../../doc-items/ItemDetailHintBar.js";
import { SaveTemplateDialog } from "./SaveTemplateDialog.js";

/** Shared with the ⋯ menu entry so either save path dismisses the same hint. */
export const SAVE_AS_TEMPLATE_HINT_KEY = "save-as-template";

/** The placeholder translators position inside the sentence. */
const ACTION_TOKEN = "{action}";

/** A character that cannot occur in copy, marking where the CTA goes. */
const ACTION_MARK = "\u0000";

/**
 * Splits the translated sentence at the point the CTA belongs.
 *
 * The copy names its action inline, so the alternatives were worse: separate
 * keys concatenated in English order (which German would not survive), or
 * Tolgee's `<T>` tag params, which render raw `<action>` markup as visible text.
 *
 * Both states have to work. Once the key is published the formatter substitutes
 * `{action}` with the mark; before that — a new key the CDN has not caught up
 * with — Tolgee falls back to the key itself *without* formatting, leaving the
 * raw `{action}` in the string. Splitting on whichever is present keeps the
 * sentence intact either way, rather than stranding the link after the full stop.
 */
const splitAtAction = (sentence: string): [string, string] => {
  const token = sentence.includes(ACTION_MARK) ? ACTION_MARK : ACTION_TOKEN;
  const [before = "", after = ""] = sentence.split(token);
  return [before, after];
};

interface Props {
  projectId: string | null;
  docId: string;
  item: {
    id: string;
    title: string;
    type: ItemType;
    deletedAt?: string | null;
    templateOriginQuoteId?: string | null;
    children: { id: string }[];
  };
  refetchQueries?: string[];
}

/**
 * Offers the save-as-template flow from the item detail panel, where a user who
 * has just built something reusable actually is — behind a ⋯ menu the feature
 * is only found by people already looking for it.
 *
 * Shown only where the offer is true: an item whose type can become a template,
 * that has content worth saving, that did not itself come from a template, and
 * whose viewer may create one. A template-sourced section is deliberately
 * excluded — its story is "update the template it came from", not "make another".
 */
export const SaveAsTemplateHintBar = ({
  projectId,
  docId,
  item,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["Templates"]);
  const { hasCapability, hasOrganisationPermission } = useUserData();

  const dismissal = useLocalStorageItemHintDismissal(
    SAVE_AS_TEMPLATE_HINT_KEY,
    docId,
    item.id
  );

  const isEligible =
    !item.deletedAt &&
    rootItemsAllowedConstraints["TEMPLATE"].includes(item.type) &&
    item.children.length > 0 &&
    !item.templateOriginQuoteId &&
    hasCapability("templating") &&
    hasOrganisationPermission("template_document_update");

  if (!isEligible || dismissal.isDismissed) return null;

  const [before, after] = splitAtAction(
    t("Reuse this setup on other quotes — {action}.", {
      ns: "Templates",
      action: ACTION_MARK,
    })
  );

  return (
    <ItemDetailHintBar
      onDismiss={dismissal.dismiss}
      message={
        <>
          {before}
          <ModalOpenButton
            Modal={SaveTemplateDialog}
            modalProps={{
              projectId,
              docId,
              sourceItemId: item.id,
              originItemId: item.id,
              sourceTitle: item.title,
              mode: { type: "create" as const },
              refetchQueries,
              // The template now exists, so the invitation to create one has to
              // stop — otherwise the bar keeps offering what the user just did.
              onSaved: dismissal.dismiss,
            }}
          >
            <Link
              component="button"
              type="button"
              underline="always"
              // A `button` element is inline-block and carries its own font, so
              // without this the CTA sits off the text baseline and in a
              // different size from the sentence it lives in.
              sx={{
                verticalAlign: "baseline",
                font: "inherit",
                textAlign: "left",
              }}
            >
              {t("Save as template", { ns: "Templates" })}
            </Link>
          </ModalOpenButton>
          {after}
        </>
      }
    />
  );
};
